import {
  getGetOwnerStatementPdfUrl,
  publishOwnerStatements,
  recordOwnerTransaction,
  runManagementFees,
  useListOwners,
  useListOwnerStatements,
  useListOwnerTransactions,
  useListProperties,
  voidOwnerTransaction,
  type OwnerTransactionKind,
  type OwnerTransactionOut,
  type StatementOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { StatRow, StatTile } from '../../components/Stats';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, formatMoney, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const KIND_LABEL: Record<OwnerTransactionKind, string> = {
  distribution: 'Paid to Owner',
  contribution: 'Received From Owner',
};

const VOID_FIELDS: Field[] = [{ name: 'reason', label: 'Why It Is Being Voided', required: true }];

function statementColumns(month: string): DataColumn<StatementOut>[] {
  return [
    { label: 'Owner', render: (s) => s.owner_name },
    { label: 'Opening', align: 'right', render: (s) => formatMoney(s.opening) },
    { label: 'Collected', align: 'right', render: (s) => formatMoney(s.receipts) },
    { label: 'Expenses', align: 'right', render: (s) => formatMoney(s.expenses) },
    { label: 'Fees', align: 'right', render: (s) => formatMoney(s.fees) },
    { label: 'Contributions', align: 'right', render: (s) => formatMoney(s.contributions) },
    { label: 'Distributions', align: 'right', render: (s) => formatMoney(s.distributions) },
    { label: 'Closing', align: 'right', render: (s) => formatMoney(s.closing) },
    {
      label: 'Published',
      render: (s) =>
        s.published_at ? (
          <Chip size="small" color="success" label={formatDateTime(s.published_at)} />
        ) : (
          <Chip size="small" label="Not Yet" />
        ),
    },
    {
      label: 'PDF',
      align: 'right',
      render: (s) => (
        <Button size="small" href={getGetOwnerStatementPdfUrl(s.owner_id, { month })}>
          PDF
        </Button>
      ),
    },
  ];
}

function MoneyDialog({ onClose }: { onClose: () => void }) {
  const owners = okItems(useListOwners({ page: 1, page_size: 200 }).data);
  const properties = okItems(useListProperties({ page: 1, page_size: 200 }).data);
  const fields: Field[] = [
    {
      name: 'kind',
      label: 'Type',
      type: 'select',
      required: true,
      options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      name: 'owner_id',
      label: 'Owner',
      type: 'select',
      required: true,
      options: owners.map((o) => ({ value: String(o.id), label: o.name })),
    },
    {
      name: 'property_id',
      label: 'Property',
      type: 'select',
      required: true,
      options: properties.map((p) => ({ value: String(p.id), label: p.name })),
    },
    { name: 'amount', label: 'Amount', type: 'number', required: true },
    { name: 'paid_on', label: 'Date', type: 'date', required: true },
    { name: 'reference', label: 'Check Number or Confirmation' },
    { name: 'memo', label: 'Memo' },
  ];
  return (
    <FormDialog
      title="Record Owner Money"
      intro="This records money already moved at your bank. It doesn't send a payment."
      fields={fields}
      initial={{
        kind: 'distribution',
        owner_id: '',
        property_id: '',
        amount: '',
        paid_on: todayIso(),
        reference: '',
        memo: '',
      }}
      onSubmit={(values) =>
        recordOwnerTransaction({
          kind: required(values, 'kind') as OwnerTransactionKind,
          owner_id: Number(values.owner_id),
          property_id: Number(values.property_id),
          amount: required(values, 'amount'),
          paid_on: required(values, 'paid_on'),
          reference: optional(values, 'reference'),
          memo: optional(values, 'memo'),
        })
      }
      onClose={onClose}
    />
  );
}

export function OwnerStatementsPage() {
  const queryClient = useQueryClient();
  const canManage = useCan('accounting.manage');
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const [recording, setRecording] = useState(false);
  const [voiding, setVoiding] = useState<OwnerTransactionOut | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const paging = useTablePage();
  const day = `${month}-01`;
  const query = useListOwnerStatements({ month: day }, { query: { enabled: month !== '' } });
  const statements = okItems(query.data);
  const transactions = okData(useListOwnerTransactions(paging.params).data);

  async function run(action: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await action());
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sum = (pick: (s: StatementOut) => string) =>
    statements.reduce((total, s) => total + Number(pick(s)), 0).toFixed(2);

  const moneyColumns: DataColumn<OwnerTransactionOut>[] = [
    { label: 'Date', render: (t) => formatDate(t.paid_on) },
    { label: 'Owner', render: (t) => t.owner_name },
    { label: 'Property', render: (t) => t.property_name },
    { label: 'Type', render: (t) => KIND_LABEL[t.kind] },
    { label: 'Reference', render: (t) => t.reference ?? '–' },
    { label: 'Amount', align: 'right', render: (t) => formatMoney(t.amount) },
    {
      label: 'Status',
      align: 'right',
      render: (t) =>
        t.voided_at ? (
          <Chip size="small" color="error" label="Voided" />
        ) : canManage ? (
          <Button size="small" color="error" onClick={() => setVoiding(t)}>
            Void
          </Button>
        ) : (
          <Chip size="small" color="success" label="Recorded" />
        ),
    },
  ];

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Owner Statements</Typography>
        <Typography color="text.secondary">
          Each owner's share of the rent collected and bills paid for their properties. Owners see a
          month only after you publish it.
        </Typography>
      </div>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          type="month"
          label="Month"
          value={month}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => setMonth(e.target.value)}
        />
        {canManage && (
          <>
            <Button
              variant="outlined"
              disabled={busy || !month}
              onClick={() =>
                void run(async () => okData(await runManagementFees({ month: day }))?.message ?? '')
              }
            >
              Update management fees
            </Button>
            <Button
              variant="contained"
              disabled={busy || !month || statements.length === 0}
              onClick={() => {
                if (!window.confirm('Publish this month, and email owners the first time?')) return;
                void run(async () => {
                  await publishOwnerStatements({ month: day });
                  return 'Statements published.';
                });
              }}
            >
              Publish to owners
            </Button>
            <Button variant="outlined" onClick={() => setRecording(true)}>
              Record owner money
            </Button>
          </>
        )}
      </Stack>
      {message && <Alert severity="info">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {query.isLoading && <CircularProgress aria-label="Loading" />}
      <StatRow>
        <StatTile label="Collected" value={formatMoney(sum((s) => s.receipts))} />
        <StatTile label="Expenses" value={formatMoney(sum((s) => s.expenses))} />
        <StatTile label="Fees" value={formatMoney(sum((s) => s.fees))} />
        <StatTile label="Distributed" value={formatMoney(sum((s) => s.distributions))} />
        <StatTile label="Owed to Owners" value={formatMoney(sum((s) => s.closing))} />
      </StatRow>
      <DataTable columns={statementColumns(day)} rows={statements} rowKey={(s) => s.owner_id} />
      {query.isSuccess && statements.length === 0 && (
        <Alert severity="info">No statements yet. Set who owns what on each property's page.</Alert>
      )}
      <Typography variant="h6">Owner Money</Typography>
      <DataTable columns={moneyColumns} rows={transactions?.items ?? []} rowKey={(t) => t.id} />
      <TablePager paging={paging} total={transactions?.total ?? 0} />
      {recording && <MoneyDialog onClose={() => setRecording(false)} />}
      {voiding && (
        <FormDialog
          title="Void This Record?"
          intro="This reverses it on the books. The record stays, marked as voided."
          fields={VOID_FIELDS}
          initial={{ reason: '' }}
          submitLabel="Void"
          onSubmit={(values) =>
            voidOwnerTransaction(voiding.id, { reason: required(values, 'reason') })
          }
          onClose={() => setVoiding(null)}
        />
      )}
    </Stack>
  );
}
